/**
 * 📦 주요재고 확인 — 한 품목씩 보고 체크하고 다음으로 (v2.198.0 · 2026-09-21 → 2차 v2.202.0)
 *
 * 사용자 요청: 「주요재고를 하나씩 모바일에서 확인하고 체크하고 다음으로 넘어가고 이런 기능의
 *   페이지를 하나 만들어서 링크 누르면 거기로 바로 들어가지게 해줘. 계정 로그인은 해야하고」
 * 2차(09-23): 「이상 있으면 메모 같은 방식으로 남기고, 다른 항목 체크로 넘어가고 … 이상했던 것만
 *   추가 조치 후에 해결되면 통과」 · 「자동으로 집계된 숫자가 이상 없게 되었을 때에 재고 알림을
 *   메신저에 보낼 수 있도록」 · 「처음부터 다시 검수하기」
 *
 * 들어오는 길
 *   · 매주 월요일 아침 웍스 입출고방으로 가는 **링크**(lib/stock/weekly-push.ts)
 *   · 홈 화면 앱 📦 재고 화면 맨 위의 [이번 주 확인] 띠
 *
 * ⭐**주소에 물음표를 쓰지 않는다**(`/m/stock-check` 하나). 로그인 뒤 돌아갈 때 물음표 뒤가
 *   버려지던 자리를 v2.198.0 에서 고쳤지만, 이미 나간 링크는 계속 돌아다닌다. 「이번 주」는
 *   서버가 정하므로 주소에 아무것도 안 붙여도 늘 맞는다.
 *
 * 보기 다섯 가지 — card(하나씩) · list(목록) · fix(🔧 조치 필요) · done(마침) · preview(📊 표 미리보기)
 *   · 처음 열 때와 **주·회차가 바뀔 때** 서버의 stage 로 고른다(checking→첫 미확인 카드 ·
 *     fixing→조치 화면 · ready/sent→마침). 화면이 단계를 스스로 계산하지 않는다.
 *   · 새 창(모달)은 만들지 않는다 — 입력은 카드 안에서, 되묻기는 window.confirm 으로.
 *
 * ⭐**다음으로 넘어가는 규칙** — [확인]·[다름]을 누르면 **아직 확인 안 한 다음 품목**으로 간다.
 *   되돌아가 고칠 수 있게 [← 이전]·[다음 →]·[목록]도 둔다(순서대로 이동 · 확인 여부 무관).
 *
 * ⭐상태 셋 — 맞음 ✅ / 조치 필요 ⚠️ / 조치 완료 🔧. 조치 필요 품목은 [확인] 한 번으로 덮을 수 없다
 *   (서버가 409). 장부를 고친 뒤 조치 화면에서 **다시 센 수 = 지금 시스템 수량**일 때만 통과된다.
 *   재고 장부 숫자는 이 화면 어디서도 바뀌지 않는다.
 *
 * 🧮 계산기(v2.207.0) — 「박스단위로 숫자가 세기 힘든게 있어서 … 사칙연산 가능하도록」.
 *   품목 카드의 현재고 옆 · 조치 카드의 지금 시스템 옆 [🧮 계산기]로 카드 안에 펼친다(새 창 아님).
 *   답을 그 숫자와 바로 견주고, 다르면 답을 수량 칸으로 넘긴다. **계산기는 기록을 남기지 않는다.**
 *   품목 카드는 **처음부터 펼쳐져 있다**(v2.207.1 · 「계산기를 기본으로 보이게 해줘. 실사할때」) — 닫으면 이 화면을
 *   새로 열 때까지 닫힌 채 · 식은 품목마다 따로 기억한다(주·회차가 바뀌면 비운다). 조회 전용은 닫힌 채로 시작.
 *
 * ⚠배포 도중 옛 서버가 답하면(gate·stage 가 없음) 옛 셈법으로 그리고 새 단추는 숨긴다.
 * ⚠조회전용(viewer)은 **보기만** 된다 — 단추를 감추고, 서버도 따로 막는다(화면 숨김 ≠ 서버 차단).
 */

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import * as checkApi from '../lib/stock-check-api';
import type {
  StockCheckItem,
  StockCheckMark,
  StockCheckWeek,
  StatusGate,
  ReasonOption,
} from '../lib/stock-check-api';
import { MobileAppTabs } from '../components/MobileAppTabs';
import { CountCalculator } from '../components/CountCalculator';
// 📷 품목 사진 — NAS 보관 (v2.224.0 · 「이 기능은 매주 진행하는 재고실사에도 반영해줘」)
import { StockPhotoStrip } from '../components/StockPhotoStrip';
import { LocationQtyFields } from '../components/LocationQtyFields';
import { draftFrom, locText, parseDraft, sameDraft } from '../lib/stock-locations';
import type { LocDraft } from '../lib/stock-locations';
import { CALC_EMPTY, calcCompare, calcEvaluate } from '../lib/count-calc';
import type { CalcState } from '../lib/count-calc';
import { dayLabel, timeLabel, kstDate, dateTimeLabel } from '../lib/date-label';
import { LoadingLines } from '../components/ui/Skeleton';

type View = 'card' | 'list' | 'fix' | 'done' | 'preview';
type Tone = 'amber' | 'emerald' | 'red' | 'blue';

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-';
  return n.toLocaleString('ko-KR');
}

/**
 * 시각 한 칸 — 오늘이면 「09:12」, 아니면 「9월 28일 (월) 09:12」.
 * 한 주 내내 쓰는 화면이라 월요일 기록을 수요일에 「09:12」로만 적으면 언제인지 모른다.
 */
function whenLabel(iso: string | null | undefined, today: string): string {
  if (!iso) return '';
  return today && kstDate(iso) === today ? timeLabel(iso) : dateTimeLabel(iso);
}

function who(name: string | null | undefined, id: string | null | undefined): string {
  return name ?? id ?? '';
}

function nameOf(it: StockCheckItem): string {
  return it.itemName ?? it.itemCode;
}

/**
 * 「그때 시스템」 숫자 = 사람이 본 수량(없으면 저장 순간 수량).
 * ⚠옛 서버는 baselineQty 를 안 준다 — 그때는 저장 순간 값으로 본다.
 */
function baselineOf(m: StockCheckMark): number | null {
  return m.baselineQty ?? m.seenQty ?? m.systemQty ?? null;
}

/** 「시스템 5 → 실제 3」 / 실제를 안 적었으면 「시스템 5 · 실제 수량 미기재」 */
function diffNumbers(baseline: number | null | undefined, actual: number | null | undefined): string {
  return actual === null || actual === undefined
    ? `시스템 ${fmt(baseline)} · 실제 수량 미기재`
    : `시스템 ${fmt(baseline)} → 실제 ${fmt(actual)}`;
}

function statusIcon(m: StockCheckMark | null): string {
  if (!m) return '⬜';
  if (m.status === 'diff') return '⚠️';
  if (m.status === 'resolved') return '🔧';
  return '✅';
}

/** 실제 수량 칸 — 비었거나 0 이상 정수(서버 상한 1,000,000 안) */
const QTY_RE = /^\d{1,6}$/;

/**
 * 방금 펼친 자리(계산기·수량 칸)를 보이게 내리는 일 — **다시 그린 직후**(layout effect)에 한다.
 * ⚠requestAnimationFrame 에 맡기지 않는다: 화면이 그려지지 않는 동안(창이 뒤에 있을 때 등)엔 아예
 *   안 돌아, 확인 창에서 「안 내려간다」로 보였다. 요청할 때 한 번 다시 그리게 해서(tick) 반드시 돈다.
 */
function useAfterRender(): (run: () => void) => void {
  const pending = useRef<(() => void) | null>(null);
  const [, setTick] = useState(0);
  useLayoutEffect(() => {
    const run = pending.current;
    if (!run) return;
    pending.current = null;
    run();
  });
  return (run) => {
    pending.current = run;
    setTick((t) => t + 1);
  };
}

/** 그 자리가 화면 밖이면 보이게 (가장 가까운 쪽으로 · 부드럽게) */
function scrollNearest(el: HTMLElement | null): void {
  el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

/** 🧮 키패드 맨 아랫줄과 아래 고정 단추 사이에 둘 간격 — 손가락이 미끄러져 [✅]가 눌리지 않게 */
const KEYPAD_GAP = 24;

const BAND_TONE: Record<Tone, string> = {
  amber: 'bg-amber-50 border-amber-300 text-amber-900',
  emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  red: 'bg-red-50 border-red-300 text-red-800',
  blue: 'bg-blue-50 border-blue-300 text-blue-900',
};

/**
 * 🧮 계산기 답이 기준 수량과 **다른데** 「맞다」 쪽 단추를 누르면 한 번 묻는다 — 센 수와 다른 「맞음」이 적히지 않게.
 * 계산기를 닫았거나, 답이 없거나, 같거나, 개수로 못 쓰는 답(소수·음수)이면 묻지 않는다.
 */
function confirmAgainstCalc(open: boolean, state: CalcState | undefined, ref: number, refLabel: string): boolean {
  if (!open || !state) return true;
  const c = calcCompare(calcEvaluate(state.expr), ref);
  if (c.kind !== 'less' && c.kind !== 'more') return true;
  return window.confirm([
    // 조사(은/는)를 붙이지 않는다 — 「지금 시스템는」처럼 틀린다(실측)
    `계산기 답 ${fmt(c.value)}개 · ${refLabel} ${fmt(ref)}개 — 서로 다릅니다.`,
    '그래도 맞다고 적을까요?',
    '',
    '다르면 [취소]를 누르고 계산기 아래 노란 단추로 센 수량을 넘겨 주세요.',
  ].join('\n'));
}

/** 🧮 계산기 여닫기 단추 — 현재고·지금 시스템 숫자 옆 (v2.207.0) */
function CalcToggle({ open, onClick, className = '' }: { open: boolean; onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={open}
      className={`px-2.5 min-h-[36px] rounded-full border text-xs font-semibold whitespace-nowrap ${
        open
          ? 'bg-blue-600 border-blue-600 text-white active:bg-blue-700'
          : 'bg-white border-slate-300 text-slate-700 active:bg-slate-100'
      } ${className}`}
    >
      {open ? '🧮 닫기' : '🧮 계산기'}
    </button>
  );
}

/** 진행 막대 아래 한 줄 알림 (409·결과 안내) */
function Band({ tone, children, onClose }: { tone: Tone; children: ReactNode; onClose?: () => void }) {
  return (
    <div className={`border-b px-3 py-2 text-xs flex items-start gap-2 ${BAND_TONE[tone]}`}>
      <div className="flex-1 min-w-0 break-words">{children}</div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="알림 닫기"
          className="shrink-0 -my-1 -mr-1 w-8 h-8 flex items-center justify-center rounded active:bg-black/5"
        >
          ✕
        </button>
      )}
    </div>
  );
}

/* ── 🔧 조치 화면의 품목 카드 한 장 ─────────────────── */

interface FixCardProps {
  item: StockCheckItem;
  mark: StockCheckMark;
  reasons: ReasonOption[];
  today: string;
  canEdit: boolean;
  /** 이번 회차 완료 보고가 나가기 전에만 되돌릴 수 있다 */
  canUndo: boolean;
  busy: boolean;
  /** 실패하면 카드 안에 보일 문구를, 성공(또는 띠로 처리)이면 null */
  onResolve: (item: StockCheckItem, input: { recountQty: number | null; reason: string; note: string }) => Promise<string | null>;
  onFixDiff: (item: StockCheckItem, actualQty: number) => Promise<string | null>;
  onUndo: (item: StockCheckItem) => void;
}

/**
 * 「그때」(발견 기록)와 「지금 시스템」을 나란히 보여 주고, 조치 내용을 골라 통과시킨다.
 *
 * ⭐세 모양 — 초록: 지금 == 적어 둔 실제 · 회색: 지금 == 그때(장부가 그대로) · 주황: 그 밖.
 * ⭐장부가 그대로면 「회수·입고·출고 처리함」은 고를 수 없다 — 서버도 거절한다(사실과 다른 기록).
 * ⭐통과는 「지금 시스템 숫자가 맞다」는 도장이다. 다른 수를 셌으면 통과 대신 이상 내용을 고친다.
 */
function FixCard({ item, mark, reasons, today, canEdit, canUndo, busy, onResolve, onFixDiff, onUndo }: FixCardProps) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [recountOpen, setRecountOpen] = useState(false);
  const [recount, setRecount] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // 🧮 다시 셀 때 쓰는 계산기 — 카드마다 따로
  const [calcOpen, setCalcOpen] = useState(false);
  const [calc, setCalc] = useState<CalcState>(CALC_EMPTY);
  const recountRef = useRef<HTMLDivElement>(null);
  const calcRef = useRef<HTMLDivElement>(null);
  const afterRender = useAfterRender();

  const now = item.currentStock;
  const baseline = baselineOf(mark);
  const actual = mark.actualQty;
  const shape: 'green' | 'gray' | 'amber' =
    actual !== null && now === actual ? 'green' : baseline !== null && now === baseline ? 'gray' : 'amber';
  const ledgerSame = baseline !== null && now === baseline;
  const isLocked = (r: ReasonOption): boolean => r.needsLedgerChange && ledgerSame;
  // 숫자가 새로 읽혀 잠긴 칩을 골라 둔 상태가 되면 「안 고름」으로 본다
  const chosen = reasons.find((r) => r.key === reason && !isLocked(r)) ?? null;
  const lockedLabels = reasons.filter(isLocked).map((r) => r.label);
  const noteOk = chosen?.key !== 'other' || note.trim() !== '';

  const rt = recount.trim();
  const recountNum = QTY_RE.test(rt) ? Number(rt) : null;
  const recountOk = !recountOpen || (recountNum !== null && recountNum === now);
  const canPass = canEdit && !busy && chosen !== null && noteOk && recountOk;
  const hint = chosen === null
    ? '조치 내용을 하나 골라 주세요'
    : !noteOk
      ? '「기타」를 골랐으면 무엇을 했는지 한 줄 적어 주세요'
      : recountOpen && rt === ''
        ? '다시 센 수량을 적어 주세요'
        : null;

  async function pass() {
    if (!canPass || !chosen) return;
    // 다른 수량 칸에 적은 수로 통과할 때는 그 수가 기준이다 — 계산기와 견주는 것은 칸을 안 열었을 때만
    if (!recountOpen && !confirmAgainstCalc(calcOpen, calc, now, '지금 시스템')) return;
    setErr(null);
    const e = await onResolve(item, {
      recountQty: recountOpen ? recountNum : null,
      reason: chosen.key,
      note: note.trim(),
    });
    if (e) setErr(e);
  }

  async function fixDiff() {
    if (recountNum === null || busy) return;
    setErr(null);
    const e = await onFixDiff(item, recountNum);
    if (e) setErr(e);
    else {
      setRecountOpen(false);
      setRecount('');
    }
  }

  /** 🧮 계산기 답 → 「다시 센 수량」 칸 (칸이 닫혀 있으면 연다) — 저장은 하지 않는다 */
  function applyCalc(qty: number) {
    setRecountOpen(true);
    setRecount(String(qty));
    afterRender(() => scrollNearest(recountRef.current));
  }

  return (
    <div id={`fix-${item.itemCode}`} className="bg-white rounded shadow-sm px-4 py-4 mb-3 scroll-mt-28">
      <div className="text-base font-bold text-slate-900 leading-snug break-words">{nameOf(item)}</div>
      <div className="text-[11px] text-slate-400 font-mono break-all mb-2">{item.itemCode}</div>

      <div className="text-xs text-slate-600 leading-relaxed">
        <span className="inline-block w-9 font-semibold text-slate-500">그때</span>
        {whenLabel(mark.checkedAt, today)} · {who(mark.checkedByName, mark.checkedBy)} · 시스템 {fmt(baseline)} ·{' '}
        {actual !== null ? `실제 ${fmt(actual)}` : '실제 수량 미기재'}
      </div>
      {mark.note && <div className="text-xs text-slate-600 pl-9 break-words">「{mark.note}」</div>}

      <div className="mt-3 bg-slate-50 rounded px-3 py-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">지금 시스템</span>
          <CalcToggle
            open={calcOpen}
            onClick={() => {
              const opening = !calcOpen;
              setCalcOpen(opening);
              if (opening) afterRender(() => scrollNearest(calcRef.current));
            }}
          />
        </span>
        <span className="text-4xl font-bold text-slate-900 tabular-nums">{fmt(now)}</span>
      </div>
      <div
        className={`mt-1.5 text-xs rounded px-2 py-1.5 ${
          shape === 'green'
            ? 'bg-emerald-50 text-emerald-800'
            : shape === 'gray'
              ? 'bg-slate-100 text-slate-600'
              : 'bg-amber-50 text-amber-800'
        }`}
      >
        {shape === 'green'
          ? `✓ 적어 둔 실제 수량(${fmt(actual)})과 같아졌습니다`
          : shape === 'gray'
            ? `그대로 ${fmt(baseline)} — 회수·입고 기록이 아직 안 고쳐졌을 수 있습니다`
            : `그때 ${fmt(baseline)} → 지금 ${fmt(now)} 로 바뀌었습니다`}
      </div>

      {calcOpen && (
        <div ref={calcRef} className="mt-2 scroll-mt-14 scroll-mb-4">
          <CountCalculator
            state={calc}
            onChange={setCalc}
            refQty={now}
            refLabel="지금 시스템"
            {...(canEdit
              ? {
                  applyLabel: (n: number) => `↓ 다시 센 수량 칸에 ${fmt(n)} 넣기`,
                  onApply: applyCalc,
                  applyWhenSame: recountOpen,
                }
              : {})}
          />
        </div>
      )}

      {!canEdit ? (
        <p className="mt-3 text-[11px] text-slate-500">조회 전용 계정은 보기만 가능합니다.</p>
      ) : (
        <>
          <div className="mt-3 text-xs font-semibold text-slate-700 mb-1.5">조치 내용 (하나 고르기)</div>
          <div className="flex flex-wrap gap-1.5">
            {reasons.map((r) => {
              const locked = isLocked(r);
              const on = chosen?.key === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  disabled={locked || busy}
                  onClick={() => setReason(on ? '' : r.key)}
                  className={`px-3 min-h-[40px] rounded-full border text-sm ${
                    on
                      ? 'bg-amber-500 border-amber-500 text-white font-semibold'
                      : locked
                        ? 'bg-slate-50 border-slate-200 text-slate-300'
                        : 'bg-white border-slate-300 text-slate-700 active:bg-slate-100'
                  }`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          {lockedLabels.length > 0 && (
            <p className="text-[11px] text-slate-500 mt-1">
              {lockedLabels.join('·')} — 장부가 그대로라 고를 수 없음
            </p>
          )}

          <label className="block text-xs font-semibold text-slate-700 mt-3 mb-1">
            메모{' '}
            <span className="font-normal text-slate-400">
              {chosen?.key === 'other' ? '(필수 — 무엇을 했는지 한 줄)' : '(선택)'}
            </span>
          </label>
          <input
            type="text"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder="예) 9/28 회수 건 입력함"
            className="w-full border border-slate-300 rounded px-3 py-2.5 text-base focus:outline-none focus:border-amber-500"
          />

          <button
            type="button"
            onClick={() => void pass()}
            disabled={!canPass}
            className="w-full mt-3 min-h-[56px] py-4 rounded font-bold text-[15px] leading-snug bg-emerald-600 text-white active:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            {busy ? '저장 중…' : `✅ 다시 세어 보니 지금 ${fmt(now)}개가 맞습니다 — 통과`}
          </button>
          {hint && <p className="text-[11px] text-slate-500 mt-1 text-center">{hint}</p>}

          <button
            type="button"
            onClick={() => { setRecountOpen((v) => !v); setRecount(''); }}
            disabled={busy}
            className="w-full mt-2 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-700 active:bg-slate-100 disabled:opacity-50"
          >
            {recountOpen ? '다른 수량 칸 닫기' : '다른 수량이었음'}
          </button>
          {recountOpen && (
            <div ref={recountRef} className="mt-2 border border-slate-200 rounded px-3 py-2.5 scroll-mb-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1">다시 센 수량</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={recount}
                onChange={(e) => setRecount(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2.5 text-base focus:outline-none focus:border-amber-500"
              />
              {rt !== '' && recountNum === null && (
                <p className="text-[11px] text-red-600 mt-1">0 이상 숫자로 적어 주세요</p>
              )}
              {recountNum !== null && recountNum === now && (
                <p className="text-[11px] text-emerald-700 mt-1">시스템 수량과 같습니다 — 위 [통과]를 누르면 됩니다</p>
              )}
              {recountNum !== null && recountNum !== now && (
                <div className="mt-1.5">
                  <p className="text-xs text-amber-800">
                    시스템 수량({fmt(now)})과 다릅니다 — 매입·출고·회수 기록을 먼저 고쳐야 통과됩니다
                  </p>
                  <button
                    type="button"
                    onClick={() => void fixDiff()}
                    disabled={busy}
                    className="w-full mt-1.5 min-h-[44px] bg-amber-50 border border-amber-400 rounded text-sm font-semibold text-amber-800 active:bg-amber-100 disabled:opacity-50"
                  >
                    이 수량으로 이상 내용 고치기
                  </button>
                </div>
              )}
            </div>
          )}

          {err && <p className="mt-2 text-xs text-red-600 break-words">{err}</p>}

          {canUndo && (
            <button
              type="button"
              onClick={() => onUndo(item)}
              disabled={busy}
              className="w-full mt-4 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-600 active:bg-slate-100 disabled:opacity-50"
            >
              ↩ 되돌리기
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ── 화면 ─────────────────────────────────────────── */

export function MobileStockCheck() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['stock-check'],
    queryFn: () => checkApi.getWeek(),
    staleTime: 0,
    // 앱으로 돌아올 때 다시 읽는 일은 아래 visibilitychange 가 맡는다(두 번 읽지 않게)
    refetchOnWindowFocus: false,
  });

  const d: StockCheckWeek | undefined = q.data;
  const items = useMemo<StockCheckItem[]>(() => d?.items ?? [], [d]);
  const canEdit = d?.canEdit ?? false;
  // ⭐옛 서버(배포 도중)는 gate·stage 를 안 준다 → 옛 셈법으로 그리고 새 단추는 숨긴다
  const gate: StatusGate | null = d && d.gate && d.stage ? d.gate : null;
  const isNew = gate !== null;
  const round = d?.round ?? 1;
  const today = d?.today ?? '';
  const doneNoticeAt = d?.doneNoticeAt ?? null;
  const reasons = d?.reasons ?? [];
  const diffChips = d?.diffChips ?? [];

  /** 셈은 서버 gate 그대로. 옛 서버면 summary 로 */
  const counts = useMemo(() => {
    if (gate) {
      return { total: gate.total, checked: gate.checked, matched: gate.matched, open: gate.open, resolved: gate.resolved };
    }
    const total = d?.summary?.total ?? items.length;
    const checked = d?.summary?.checked ?? items.filter((i) => i.check !== null).length;
    const open = d?.summary?.diff ?? items.filter((i) => i.check?.status === 'diff').length;
    return { total, checked, matched: Math.max(0, checked - open), open, resolved: 0 };
  }, [gate, d, items]);
  const allChecked = gate ? gate.block !== 'unchecked' : counts.total > 0 && counts.checked >= counts.total;

  const [idx, setIdx] = useState(0);
  const [view, setView] = useState<View>('card');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: Tone; text: string } | null>(null);
  const [moved, setMoved] = useState<{ itemCode: string; itemName: string; seen: number; now: number } | null>(null);

  // 「다름」 입력판 — 품목을 옮기면 비운다(앞 품목의 값이 따라가면 안 된다)
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffInit, setDiffInit] = useState({ actual: '', note: '' });
  const [actualQty, setActualQty] = useState('');
  const [note, setNote] = useState('');
  const [sameAck, setSameAck] = useState(false);
  // 📍 장소별 센 수(v2.226.0) — 품목마다 · 옮기면 비우고 저장된 값으로 다시 채운다
  const [locState, setLocState] = useState<{ code: string; draft: LocDraft; active: string } | null>(null);
  const [memoOpen, setMemoOpen] = useState(false);

  const [onlyOpen, setOnlyOpen] = useState(false);
  const [fixFocus, setFixFocus] = useState<string | null>(null);

  // 🧮 계산기 — 실사하는 사람(기록 가능)에겐 **처음부터 펼쳐 둔다**(v2.207.1). 사람이 여닫으면 그 뒤로는 그 선택을
  //   따른다(다음 품목에서도 그대로 · 새로고침하면 다시 기본). canEdit 은 응답이 와야 알 수 있어 「고른 값 ?? 기본」으로 계산한다.
  //   식은 품목마다 따로 (← 이전으로 돌아가도 남는다)
  const [calcPref, setCalcPref] = useState<boolean | null>(null);
  const calcOpen = calcPref ?? canEdit;
  const [calcByItem, setCalcByItem] = useState<Record<string, CalcState>>({});
  const diffPanelRef = useRef<HTMLDivElement>(null);
  const calcRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const bandsRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const errRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const afterRender = useAfterRender();

  /**
   * 🧮 키패드가 아래 고정 단추에 가리지 않게 내린다 — 단, **안내 띠·품목 이름이 머리글 밑으로 숨을 만큼은 안 내린다.**
   * ⚠scrollIntoView(nearest) 로만 내리면 낮은 화면(웍스 앱 안 브라우저 등)에서 품목 이름과 「그 사이 숫자가
   *   바뀜」 같은 띠가 머리글 밑으로 숨었다(v2.207.1 검토에서 짚음). **무엇을 세는지가 먼저**다 — 둘 다 못 들어오는
   *   화면이면 이름·띠를 남기고 키패드 끝이 조금 가린다(손으로 조금 올리면 된다).
   * 간격은 고정 단추의 **실제 높이**로 잰다 — 아이폰 홈 막대(safe-area)만큼 단추가 높아져도 KEYPAD_GAP 은 띄운다.
   * 지켜야 할 위 끝 = 안내 띠가 있으면 **띠의 맨 위**, 없으면 **품목 이름**(그 위 「몇 번째 품목」 줄은 넘겨도 된다 —
   *   낮은 화면에서 숫자판 맨 아랫줄까지 들어오게 하려면 그만큼이 필요했다 · 실측).
   */
  function revealKeypad() {
    const calc = calcRef.current;
    const dock = dockRef.current;
    const anchor = bandsRef.current;
    const head = headerRef.current;
    if (!calc || !dock || !anchor || !head) return;
    const need = calc.getBoundingClientRect().bottom + KEYPAD_GAP - dock.getBoundingClientRect().top;
    const bandsBox = anchor.getBoundingClientRect();
    const keepTop = bandsBox.height > 0 ? bandsBox.top : (nameRef.current?.getBoundingClientRect().top ?? bandsBox.top) - 4;
    const room = keepTop - head.getBoundingClientRect().bottom;
    const by = Math.min(need, room);
    if (by > 0) window.scrollBy({ top: by });
  }

  /** 🧮 여닫기 — 열 때는 키패드 끝까지 화면에 들어오게 내린다 */
  function toggleCalc() {
    const opening = !calcOpen;
    setCalcPref(opening);
    if (opening) afterRender(revealKeypad);
  }

  // 📊 미리보기 — t 는 **연 순간 한 번만** 정한다(렌더마다 바꾸면 표를 매번 새로 그린다)
  const [previewT, setPreviewT] = useState<number | null>(null);
  const [previewFrom, setPreviewFrom] = useState<View>('done');
  const [imgState, setImgState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [zoom, setZoom] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // 🔔 시험 발송 (admin 전용)
  const isAdmin = user?.role === 'admin';
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  // ✅ 완료 보고 다시 보내기
  const [retryBusy, setRetryBusy] = useState(false);

  function clearDiff() {
    setDiffOpen(false);
    setDiffInit({ actual: '', note: '' });
    setActualQty('');
    setNote('');
    setSameAck(false);
    setLocState(null);
    setMemoOpen(false);
  }

  /**
   * ⭐보기 고르기 — 처음 열 때, 그리고 **주(weekDate)나 회차(round)가 바뀔 때마다** 다시 고른다.
   *   누가 [처음부터 다시]를 눌렀거나 월요일이 지나 새 주가 되면, 옛 자리(idx·조치 화면)를
   *   그대로 두면 엉뚱한 품목을 가리킨다.
   */
  const pickedKey = useRef<string | null>(null);
  const selfRestart = useRef(false);
  useEffect(() => {
    if (!d || items.length === 0) return;
    const key = `${d.weekDate}:${d.round ?? 1}`;
    const prev = pickedKey.current;
    if (prev === key) return;
    pickedKey.current = key;
    clearDiff();
    setErr(null);
    setMoved(null);
    // 앞 회차·지난주의 발송 결과 문구가 새 회차에 「✅ 보냈습니다」로 남지 않게 (v2.202.1)
    setStatusMsg(null);
    setSendResult(null);
    setTestMsg(null);
    setOnlyOpen(false);
    setFixFocus(null);
    // 앞 주·앞 회차에 센 식이 새 회차 카드에 남아 있으면 이번에 센 것처럼 보인다
    setCalcByItem({});
    const st = d.gate && d.stage ? d.stage : null;
    if (st === 'fixing') setView('fix');
    else if (st === 'ready' || st === 'sent') setView('done');
    else {
      const first = items.findIndex((i) => i.check === null);
      if (first !== -1) {
        setIdx(first);
        setView('card');
      } else {
        // 가리킬 품목이 없으면 흰 화면 대신 목록(옛 서버는 옛처럼 마침 화면)
        setView(st === null ? 'done' : 'list');
      }
    }
    if (prev === null) setNotice(null);
    else if (prev.split(':')[0] !== d.weekDate) {
      setNotice({ tone: 'blue', text: `새 주가 시작되었습니다 — ${dayLabel(d.weekDate)} 주간 확인으로 바꿨습니다` });
    } else if (selfRestart.current) {
      setNotice({ tone: 'blue', text: `${d.round}회차를 시작합니다 — 첫 품목부터 다시 세어 주세요` });
    } else {
      setNotice({
        tone: 'amber',
        text: `누군가 ‘처음부터 다시 검수하기’를 눌러 ${d.round}회차가 시작되었습니다 — 첫 품목부터 다시 봅니다`,
      });
    }
    selfRestart.current = false;
  }, [d, items]);

  /** 앱으로 돌아오면(창고에서 폰을 다시 켜면) 새로 읽는다 — 그 사이 누가 기록을 고쳤을 수 있다 */
  const refetchRef = useRef(q.refetch);
  useEffect(() => {
    refetchRef.current = q.refetch;
  });
  useEffect(() => {
    function onVis() {
      if (document.visibilityState === 'visible') void refetchRef.current();
    }
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const curCode = items[idx]?.itemCode;
  /** 보기·품목을 옮기면 맨 위로 — 조치 화면에서 품목을 가리켜 들어왔으면 그 카드로 */
  useEffect(() => {
    if (view === 'fix' && fixFocus) {
      document.getElementById(`fix-${fixFocus}`)?.scrollIntoView({ block: 'start' });
      return;
    }
    window.scrollTo({ top: 0 });
    // 🧮 계산기를 연 채로 다음 품목에 오면 키패드 끝까지 보이게 — 작은 폰에서 아래 고정 단추에 가리지 않게.
    //   (다 들어오는 화면이면 움직이지 않는다 · 계산기가 닫혀 있으면 calcRef 가 비어 있다 · 이름·띠 위로는 안 내린다)
    if (view === 'card') revealKeypad();
    // ⭐curCode — 처음 열 때 첫 품목이 0번이면 idx·view 가 안 바뀌어 여기가 안 돈다(계산기가 기본으로 펼쳐지므로
    //   첫 화면에서도 키패드가 가리지 않게 · v2.207.1). 새로 읽기만 하면 품목이 같아 다시 돌지 않는다.
  }, [view, idx, fixFocus, curCode]);

  /**
   * 카드 화면에서 안내 띠(409·404 등 서버 문구)가 새로 뜨면 맨 위로 — 키패드를 보느라 내려가 있으면 띠가
   * 머리글 밑에 숨어 「눌렀는데 아무 일도 없다」로 보인다 (v2.207.1 · 계산기가 기본으로 펼쳐진 뒤 생긴 자리).
   */
  const noticeText = notice?.text ?? null;
  useEffect(() => {
    if (noticeText && view === 'card') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [noticeText, view]);

  /** 저장 실패 문구는 카드 맨 아래에 뜬다 — 계산기가 펼쳐져 있으면 화면 밖이라 보이게 내린다 */
  useEffect(() => {
    if (err && view === 'card') scrollNearest(errRef.current);
  }, [err, view]);

  const cur = items[idx] as StockCheckItem | undefined;

  /* 「다름」 입력판 값 */
  const actualTrim = actualQty.trim();
  const actualBad = actualTrim !== '' && !QTY_RE.test(actualTrim);
  const actualNum = actualTrim !== '' && !actualBad ? Number(actualTrim) : null;
  const same = !!cur && actualNum !== null && actualNum === cur.currentStock;
  const diffCanSave = !actualBad && (actualNum !== null || note.trim() !== '') && (!same || sameAck);
  const diffDirty = diffOpen && (actualTrim !== diffInit.actual || note.trim() !== diffInit.note);

  /* 📍 장소별 센 수 (v2.226.0) — 서버가 장소 목록을 주면 처음 확인(·다시 확인)은 두 칸으로 센다.
     「조치 필요」 품목(다시 세고 통과 · 다름 내용 고치기)은 예전 흐름 그대로. */
  const locations = useMemo(() => d?.locations ?? [], [d]);
  const curMark = cur?.check ?? null;
  const locMode = locations.length > 0 && !!cur && (curMark === null || curMark.status === 'ok');
  const savedLocDraft = draftFrom(curMark?.locQty ?? null, locations);
  const locDraft: LocDraft = cur && locState?.code === cur.itemCode ? locState.draft : savedLocDraft;
  const locActive = cur && locState?.code === cur.itemCode ? locState.active : (locations[0]?.key ?? '');
  const locParsed = parseDraft(locDraft, locations);
  const locDirty = locMode && !sameDraft(locDraft, savedLocDraft);
  function setLocDraft(next: LocDraft) { if (cur) setLocState({ code: cur.itemCode, draft: next, active: locActive }); }
  function setLocActive(key: string) { if (cur) setLocState({ code: cur.itemCode, draft: locDraft, active: key }); }
  const locLabel = (key: string): string => locations.find((l) => l.key === key)?.label ?? key;

  /** 적던 입력이 있으면 한 번 묻는다 */
  function confirmLeave(): boolean {
    if (locDirty) return window.confirm('적던 장소별 수량이 아직 저장되지 않았습니다. 옮기면 사라집니다 — 옮길까요?');
    if (!diffDirty) return true;
    return window.confirm('적던 실제 수량·메모가 사라집니다. 옮길까요?');
  }

  function goto(next: number) {
    clearDiff();
    setErr(null);
    setNotice(null);
    setIdx(Math.max(0, Math.min(items.length - 1, next)));
    setView('card');
  }

  function openView(v: View) {
    clearDiff();
    setErr(null);
    setNotice(null);
    setView(v);
  }

  function goFirstUnchecked() {
    const first = items.findIndex((i) => i.check === null);
    if (first === -1) openView('list');
    else goto(first);
  }

  /** 🔧 조치 화면 — 「지금 시스템」 숫자가 핵심이라 들어올 때마다 새로 읽는다 */
  function enterFix(code?: string) {
    clearDiff();
    setErr(null);
    setNotice(null);
    setFixFocus(code ?? null);
    setView('fix');
    void q.refetch();
  }

  function openDiff() {
    const m = cur?.check ?? null;
    // 이미 「조치 필요」면 적어 둔 내용을 채워서 연다(내용 고치기)
    const init = m && m.status === 'diff'
      ? { actual: m.actualQty !== null ? String(m.actualQty) : '', note: m.note ?? '' }
      : { actual: '', note: '' };
    setDiffInit(init);
    setActualQty(init.actual);
    setNote(init.note);
    setSameAck(false);
    setErr(null);
    setDiffOpen(true);
    // ⭐입력판이 계산기·세부 칸 **아래**에 열린다 — 계산기가 기본으로 펼쳐진 뒤(v2.207.1)로는 390×844 폰에서도
    //   「실제로 센 수량」 칸이 아래 고정 단추 뒤로 숨었다(실측 · 검토에서 짚음). 열 때마다 보이게 내린다.
    afterRender(() => scrollNearest(diffPanelRef.current));
  }

  /**
   * 🧮 계산기 답 → 「실제로 센 수량」 칸. 입력판이 닫혀 있으면 연다(적어 둔 다름 내용이 있으면 채워서).
   * ⭐저장은 하지 않는다 — 늘 아래 [⚠ 다름으로 적고 다음 →]으로. 열린 입력판이 화면 밖이면 보이게 내린다.
   */
  function applyCalcQty(qty: number) {
    if (!cur || !canEdit) return;
    // 📍 장소 칸 모드 — 🧮 답은 고른 칸(파란 테두리)에 들어간다. 저장은 아래 단추로
    if (locMode && !diffOpen) {
      setLocDraft({ ...locDraft, [locActive]: String(qty) });
      return;
    }
    if (!diffOpen) openDiff();
    setActualQty(String(qty));
    setSameAck(false);
    afterRender(() => scrollNearest(diffPanelRef.current));
  }

  function addChip(c: string) {
    setNote((prev) => {
      const t = prev.trim();
      if (t.includes(c)) return prev;
      return t ? `${t} · ${c}` : c;
    });
  }

  /**
   * 실패 처리 — 409(다른 곳에서 먼저 바뀜·숫자가 바뀜)는 **새로 읽고 서버 문구를 띠로** 보인다.
   * 그 밖은 문구를 돌려줘 부르는 쪽이 제자리에 적게 한다.
   */
  async function onFail(e: unknown): Promise<string | null> {
    const msg = e instanceof Error ? e.message : String(e);
    // ⚠404(이번 주 대상이 아님·기록이 없음)도 「화면이 낡았다」는 뜻이다 — 새로 안 읽으면 같은 단추를 계속 누른다 (v2.202.1)
    if (e instanceof checkApi.CheckApiError && (e.status === 409 || e.status === 404)) {
      await q.refetch();
      setNotice({ tone: 'amber', text: msg });
      return null;
    }
    return msg;
  }

  /**
   * 저장 뒤 — 아직 확인 안 한 **다음** 품목으로. 없으면 마침 화면.
   *
   * ⭐**앞쪽부터 찾지 않는다** — 뒤로 돌아가 이미 확인한 품목을 고친 뒤에도 앞으로 나아가야지,
   *   맨 앞으로 튀면 「방금 지나온 자리」로 되돌아간 것처럼 보인다. 지금 자리 다음부터 보고,
   *   끝까지 없으면 그때 앞쪽을 훑는다.
   * ⚠새로 읽기에 실패하면 옛 목록이 온다 — 그래서 방금 누른 품목은 코드로 빼 준다.
   */
  function advance(list: StockCheckItem[], justCode: string) {
    const here = list.findIndex((i) => i.itemCode === justCode);
    const from = here === -1 ? idx : here;
    const open = (i: StockCheckItem): boolean => i.check === null && i.itemCode !== justCode;
    let next = list.findIndex((i, n) => n > from && open(i));
    if (next === -1) next = list.findIndex((i, n) => n < from && open(i));
    if (next === -1) openView('done');
    else goto(next);
  }

  async function save(status: 'ok' | 'diff') {
    if (!cur || busy || !canEdit) return;
    const item = cur;
    setBusy(true);
    setErr(null);
    setMoved(null);
    try {
      const r = await checkApi.mark({
        itemCode: item.itemCode,
        status,
        actualQty: status === 'diff' ? actualNum : null,
        note: status === 'diff' ? note.trim() || null : null,
        // ⭐사람이 본 숫자 — 저장 순간 장부와 달라도 막지 않고 둘 다 남긴다
        seenQty: item.currentStock,
      });
      const mv = r.moved ?? null;
      if (mv) setMoved({ itemCode: item.itemCode, itemName: nameOf(item), seen: mv.seen, now: mv.now });
      const fresh = await q.refetch();
      advance(fresh.data?.items ?? items, item.itemCode);
    } catch (e) {
      const m = await onFail(e);
      if (m) setErr(m);
    } finally {
      setBusy(false);
    }
  }

  /**
   * 📍 장소별 수량으로 저장 (v2.226.0) — 한 칸 이상 필수 · 빈 칸 0 · **서버가 합계로 확인/다름 판정**.
   * 다름이면 실제 수량 = 합계, 메모(📝)는 함께. 다름이 되면 그 품목은 예전과 같이 「조치 필요」 흐름으로 간다.
   */
  async function saveLoc() {
    if (!cur || busy || !canEdit || !locMode) return;
    const item = cur;
    const p = locParsed;
    if (p.bad || !p.any) return;
    setBusy(true);
    setErr(null);
    setMoved(null);
    try {
      const r = await checkApi.mark({
        itemCode: item.itemCode,
        status: p.sum === item.currentStock ? 'ok' : 'diff',
        locQty: p.qty,
        // 📝 메모는 맞음이어도 남는다 (v2.322.0 · #31) — 메모 칸을 안 열고 다시 확인하면 지난번 메모를 그대로 둔다(열고 비우면 지움)
        note: note.trim() || (!memoOpen ? item.check?.note ?? null : null),
        seenQty: item.currentStock,
      });
      const mv = r.moved ?? null;
      if (mv) setMoved({ itemCode: item.itemCode, itemName: nameOf(item), seen: mv.seen, now: mv.now });
      const fresh = await q.refetch();
      advance(fresh.data?.items ?? items, item.itemCode);
    } catch (e) {
      const m = await onFail(e);
      if (m) setErr(m);
    } finally {
      setBusy(false);
    }
  }

  /**
   * [↩ 되돌리기] — 맞음은 바로, **조치 필요는 무엇을 되돌리는지 적어 한 번 묻는다**.
   * 서버는 줄을 지우는 대신 「되돌림」 사건을 남긴다. 완료 보고가 나간 뒤에는 서버가 409.
   */
  async function undo(item: StockCheckItem) {
    const m = item.check;
    if (!m || busy || !canEdit) return;
    if (m.status === 'diff') {
      const what = [diffNumbers(baselineOf(m), m.actualQty), m.note].filter(Boolean).join(' · ');
      const ok = window.confirm(
        `${nameOf(item)} — ${who(m.checkedByName, m.checkedBy)} ${whenLabel(m.checkedAt, today)} 기록(${what})을 되돌립니다.\n`
        + '기록은 이력에 ‘되돌림’으로 남습니다. 되돌릴까요?',
      );
      if (!ok) return;
    }
    setBusy(true);
    setErr(null);
    try {
      await checkApi.unmark(item.itemCode);
      await q.refetch();
      if (view === 'fix') {
        setNotice({ tone: 'blue', text: `${nameOf(item)} — 되돌렸습니다. 이 품목은 다시 세어 적어 주세요` });
      }
    } catch (e) {
      const msg = await onFail(e);
      if (msg) {
        if (view === 'fix') setNotice({ tone: 'red', text: msg });
        else setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  /** [↩ 조치 완료 취소] → 조치 필요로 */
  async function unresolveItem(item: StockCheckItem) {
    const m = item.check;
    if (!m || m.status !== 'resolved' || busy || !canEdit) return;
    const r = m.resolved;
    const what = r ? `${who(r.byName, r.by)} ${whenLabel(r.at, today)} · 다시 세어 ${fmt(r.qty)}` : '';
    const ok = window.confirm(
      `${nameOf(item)} — 통과 기록(${what})을 되돌려 ‘조치 필요’로 돌립니다.\n기록은 이력에 남습니다. 되돌릴까요?`,
    );
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await checkApi.unresolve(item.itemCode);
      await q.refetch();
    } catch (e) {
      const msg = await onFail(e);
      if (msg) setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  /** 🔧 다시 세고 통과 — 화면의 「지금 시스템」 숫자를 함께 보내 그 사이 바뀌었으면 서버가 409 */
  async function resolveItem(
    item: StockCheckItem,
    input: { recountQty: number | null; reason: string; note: string },
  ): Promise<string | null> {
    if (busy || !canEdit) return null;
    setBusy(true);
    setNotice(null);
    try {
      await checkApi.resolve({
        itemCode: item.itemCode,
        seenQty: item.currentStock,
        recountQty: input.recountQty,
        reason: input.reason,
        note: input.note || null,
      });
      await q.refetch();
      setNotice({ tone: 'emerald', text: `✅ ${nameOf(item)} — 조치 완료로 적었습니다` });
      return null;
    } catch (e) {
      return await onFail(e);
    } finally {
      setBusy(false);
    }
  }

  /** [이 수량으로 이상 내용 고치기] — 발견자·그때 수량은 서버가 그대로 두고 실제 수량만 고친다(메모 유지) */
  async function fixDiffItem(item: StockCheckItem, actual: number): Promise<string | null> {
    const m = item.check;
    if (!m || busy || !canEdit) return null;
    setBusy(true);
    setNotice(null);
    try {
      await checkApi.mark({
        itemCode: item.itemCode,
        status: 'diff',
        actualQty: actual,
        note: m.note ?? null,
        seenQty: item.currentStock,
      });
      await q.refetch();
      setNotice({
        tone: 'amber',
        text: `${nameOf(item)} — 실제 ${fmt(actual)}로 고쳐 적었습니다. 기록을 고친 뒤 다시 통과시켜 주세요`,
      });
      return null;
    } catch (e) {
      return await onFail(e);
    } finally {
      setBusy(false);
    }
  }

  /**
   * 🔄 처음부터 다시 검수하기 (관리 이상).
   * ⭐조치 필요가 남아 있어도 누를 수 있다 — 대신 확인 창에 그 품목들을 적고, 다음 회차 완료 보고에
   *   그 사실이 적힌다는 것을 알린다. 앞 회차 기록은 지워지지 않고 이력에 남는다.
   */
  async function restartRound() {
    if (!d || !isNew || busy || !d.canRestart) return;
    const openItems = items.filter((i) => i.check?.status === 'diff');
    const parts = [`맞음 ${counts.matched}`];
    if (counts.open > 0) parts.push(`조치 필요 ${counts.open}`);
    if (counts.resolved > 0) parts.push(`조치 완료 ${counts.resolved}`);
    const lines = [
      '처음부터 다시 셉니다.',
      '',
      `· 지금까지 적은 ${counts.checked}건(${parts.join(' · ')})은 지워지지 않고 ${round}회차 기록으로 이력에 남습니다`,
      `· 화면은 0/${counts.total}부터 다시 시작합니다`,
      // ⚠이번 회차 보고가 아직 안 나갔으면 「한 번 더」는 거짓이다 (v2.202.1)
      doneNoticeAt
        ? `· 다 채우면 앞 보고(${timeLabel(doneNoticeAt)})를 대신하는 완료 보고가 메시지방에 한 번 더 나갑니다 (${round + 1}회차)`
        : `· 다 채우면 메시지방에 완료 보고가 나갑니다 (${round + 1}회차)`,
    ];
    if (openItems.length > 0) {
      const names = openItems.slice(0, 5).map(nameOf).join(', ')
        + (openItems.length > 5 ? ` 외 ${openItems.length - 5}건` : '');
      lines.push('', `⚠ 조치 필요 ${openItems.length}건이 풀리지 않은 채입니다: ${names}`, '다음 회차 완료 보고에 이 사실이 적힙니다');
    }
    lines.push('', '다시 시작할까요?');
    if (!window.confirm(lines.join('\n'))) return;
    setBusy(true);
    setNotice(null);
    try {
      selfRestart.current = true;
      await checkApi.restart(round);
      // 다시 읽으면 회차가 바뀌어 위의 보기 고르기가 첫 카드로 데려간다
      await q.refetch();
    } catch (e) {
      selfRestart.current = false;
      const msg = await onFail(e);
      if (msg) setNotice({ tone: 'red', text: msg });
    } finally {
      setBusy(false);
    }
  }

  /**
   * ✅⚠ 완료 보고 다시 보내기 (v2.201.0 · 2차에서 회차를 싣는다).
   * 웍스가 막혀 보고가 실패하면 담당자는 나간 줄 알고 방 사람들은 안 끝난 줄 안다 — 그래서 결과를 보여 준다.
   * ⭐force — 「나갔는지 모름」일 때만 (v2.202.2). **방에 없는 것을 확인했나**를 한 번 묻는다 — 웍스가 글을
   *   올려 놓고 답만 못 준 경우라면 누르는 순간 같은 글이 두 번 올라간다.
   */
  async function retryNotice(force = false) {
    if (retryBusy) return;
    if (force && !window.confirm([
      '메시지방에 이번 완료 보고가 없는 것을 확인하셨나요?',
      '',
      '· 웍스가 답을 주지 않아, 보고가 나갔는지 확실하지 않은 상태입니다',
      '· 방에 이미 올라와 있는데 누르면 같은 글이 두 번 올라갑니다',
      '',
      '방에 없을 때만 [확인]을 눌러 주세요.',
    ].join('\n'))) return;
    setRetryBusy(true);
    try {
      await checkApi.retryNotice(isNew ? round : undefined, force);
      await q.refetch();
    } catch (e) {
      const msg = await onFail(e);
      if (msg) setNotice({ tone: 'red', text: `다시 보내지 못했습니다 — ${msg}` });
      // 거절돼도 새로 읽는다 — 그 사이 다른 사람이 보냈으면 딱지가 바뀐다 (v2.202.1)
      void q.refetch();
    } finally {
      setRetryBusy(false);
    }
  }

  /** 📊 미리보기 열기 — 이것이 「보내기 전 확인」이다(예전의 되묻는 창은 없앴다) */
  function openPreview() {
    setPreviewFrom(view === 'preview' ? previewFrom : view);
    setPreviewT(Date.now());
    setImgState('loading');
    setZoom(false);
    setSendResult(null);
    clearDiff();
    setNotice(null);
    setView('preview');
  }

  function reloadPreview() {
    setPreviewT(Date.now());
    setImgState('loading');
  }

  /**
   * 📊 재고현황 표 보내기 — 서버가 그 순간 수량·시각으로 표를 **새로 만들어** 보낸다.
   * ⭐여러 번 보낼 수 있다(수량을 고친 뒤 다시). 1분 안에 또 누르면 서버가 409 로 막는다.
   * ⚠실패 문구는 서버 것을 그대로 — 「보냈는지 확실하지 않습니다」를 「실패」로 바꿔 적으면 또 누른다.
   */
  async function sendStatusNow() {
    if (statusBusy || !canEdit) return;
    setStatusBusy(true);
    setSendResult(null);
    try {
      const r = await checkApi.sendStatus();
      const res = { ok: true, text: `✅ 보냈습니다 — 주요재고 ${r.itemCount}종` };
      setSendResult(res);
      setStatusMsg(res);
    } catch (e) {
      const res = { ok: false, text: e instanceof Error ? e.message : String(e) };
      setSendResult(res);
      setStatusMsg(res);
    } finally {
      setStatusBusy(false);
      void q.refetch();
    }
  }

  /**
   * 🔔 시험 발송 — 바깥(메시지방)으로 나가는 동작이라 **한 번 묻고** 보낸다.
   * 보낸 글을 그대로 화면에 보여 준다 — 「무엇이 갔나」를 눈으로 확인할 수 있어야 한다.
   */
  async function sendTest() {
    if (testBusy) return;
    if (!window.confirm('메시지방(입출고방)으로 시험 발송합니다.\n맨 앞에 [시험발송]이 붙습니다. 보낼까요?')) return;
    setTestBusy(true);
    setTestMsg(null);
    try {
      const r = await checkApi.sendTest();
      setTestMsg(`✅ 보냈습니다\n\n${r.text}`);
    } catch (e) {
      setTestMsg(`❌ 보내지 못했습니다 — ${(e as Error).message}`);
    } finally {
      setTestBusy(false);
    }
  }

  /* ── 머리글 (모든 화면 공통) ───────────────────── */
  const header = (
    <header ref={headerRef} className="sticky top-0 z-20 bg-slate-900 text-white px-3 py-2 shadow">
      <div className="flex items-center justify-between gap-2">
        <h1 className="sr-only">주요재고 확인</h1>
        <MobileAppTabs />
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-300 truncate max-w-[6rem]">{user?.name ?? user?.userId ?? ''}</span>
          <Link
            to="/m/inventory"
            className="px-2 py-1 bg-slate-700 active:bg-slate-600 rounded text-[11px] whitespace-nowrap"
          >
            재고 목록
          </Link>
        </div>
      </div>
    </header>
  );

  const progress = (
    <div className="bg-white border-b border-slate-200 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-xs mb-1.5">
        <span className="font-semibold text-slate-700">
          📦 주요재고 확인 · {d ? dayLabel(d.weekDate) : ''} 주간{round >= 2 ? ` · ${round}회차` : ''}
        </span>
        <span className="text-slate-500 whitespace-nowrap">
          <b className="text-slate-900">{counts.checked}</b> / {counts.total}
          {counts.open > 0 && <span className="text-amber-600 font-semibold"> · 조치 필요 {counts.open}</span>}
          {counts.resolved > 0 && <span className="text-slate-600"> · 조치 완료 {counts.resolved}</span>}
        </span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500 transition-all"
          style={{ width: counts.total ? `${Math.min(100, (counts.checked / counts.total) * 100)}%` : '0%' }}
        />
      </div>
    </div>
  );

  /** 진행 막대 아래 띠 — 새로 읽기 실패 · 409 등 서버 문구 · 「그 사이 숫자가 바뀜」 */
  const bands = (
    // ref — 🧮 키패드를 보이게 내릴 때 이 띠 묶음(없으면 카드 맨 위)이 머리글 밑으로 숨지 않게 하는 기준
    <div ref={bandsRef}>
      {q.isError && q.data && (
        <Band tone="red">최신 정보를 불러오지 못했습니다 — {(q.error as Error).message}</Band>
      )}
      {notice && (
        <Band tone={notice.tone} onClose={() => setNotice(null)}>
          {notice.text}
        </Band>
      )}
      {moved && (
        <Band tone="amber" onClose={() => setMoved(null)}>
          <div>
            방금 적은 <b>{moved.itemName}</b> — 화면 {fmt(moved.seen)} · 저장 순간 {fmt(moved.now)} (그 사이
            입출고가 있었습니다). 기록엔 둘 다 남았습니다
          </div>
          <button
            type="button"
            onClick={() => {
              const n = items.findIndex((i) => i.itemCode === moved.itemCode);
              if (n === -1 || !confirmLeave()) return;
              setMoved(null);
              goto(n);
            }}
            className="mt-1.5 px-3 min-h-[36px] bg-white border border-amber-400 rounded text-xs font-semibold text-amber-800 active:bg-amber-100"
          >
            그 품목 다시 보기
          </button>
        </Band>
      )}
    </div>
  );

  /* ── 📊 현황표 영역 (마침·목록 두 곳 · 서버 gate 그대로) ── */
  const lastSend = d?.lastStatusSend ?? null;
  let lastSendLine: ReactNode = null;
  if (lastSend) {
    const t = whenLabel(lastSend.sentAt, today);
    const res = lastSend.result ?? 'sent';
    const other = isNew && lastSend.round && lastSend.round !== round ? ` (${lastSend.round}회차)` : '';
    lastSendLine = res === 'sent' ? (
      <p className="text-[11px] text-slate-400 mt-0.5 text-center">
        마지막 발송 {t} · {lastSend.sentByName}
        {lastSend.itemCount ? ` · ${lastSend.itemCount}종` : ''}
        {other}
      </p>
    ) : res === 'failed' ? (
      <p className="text-[11px] text-red-600 mt-0.5 text-center">❌ {t} 표를 만들지 못해 보내지 않았습니다{other}</p>
    ) : (
      <p className="text-[11px] text-amber-700 mt-0.5 text-center">
        ⚠ {t} 발송 결과가 확실하지 않습니다 — 메시지방을 먼저 확인해 주세요{other}
      </p>
    );
  }

  /**
   * ⭐다 되기 전에는 **감추지 않고 잠근다** — 왜 못 누르는지 알려 줘야 한다.
   * ⭐마지막 발송 기록을 함께 보인다 — 여럿이 보는 화면이라 없으면 또 누른다.
   */
  const statusArea = !canEdit || !gate ? null : (
    <div className="mt-2">
      <button
        type="button"
        onClick={openPreview}
        disabled={!gate.pass || busy}
        className={
          gate.pass
            ? 'w-full min-h-[52px] bg-blue-600 text-white rounded font-semibold active:bg-blue-700 disabled:opacity-50'
            : 'w-full min-h-[52px] bg-slate-100 text-slate-400 rounded font-semibold border border-slate-200'
        }
      >
        📊 표 미리 보고 보내기
      </button>
      <p className="text-[11px] text-slate-500 mt-1 text-center">
        {gate.pass
          ? '주요재고 전체 수량표가 예전 월요일 안내와 같은 모양으로 나갑니다.'
          : gate.block === 'open_diff'
            ? `조치 필요 ${gate.open}건을 통과시키면 보낼 수 있습니다.`
            : `${gate.total}종을 모두 확인하면 보낼 수 있습니다 (지금 ${gate.checked}/${gate.total}).`}
      </p>
      {gate.block === 'open_diff' && (
        <button
          type="button"
          onClick={() => enterFix()}
          className="w-full mt-1.5 min-h-[44px] bg-amber-50 border border-amber-300 rounded text-sm font-semibold text-amber-800 active:bg-amber-100"
        >
          🔧 조치 필요 {gate.open}건 보기
        </button>
      )}
      {lastSendLine}
      {statusMsg && (
        <p className={`text-xs mt-1 text-center break-words ${statusMsg.ok ? 'text-emerald-700' : 'text-red-600'}`}>
          {statusMsg.text}
        </p>
      )}
    </div>
  );

  /**
   * ✅⚠ 완료 보고가 방에 나갔나 — 다 확인한 뒤에만.
   * ⭐판정은 서버(noticeState)가 한다 — 보냄 · 보내는 중 · **나갔는지 모름** · 못 보냄/아직 (v2.202.2)
   */
  const noticeKind = d ? checkApi.noticeStateOf(d) : 'none';
  const noticeBlock = !allChecked ? null : noticeKind === 'sent' ? (
    <p className="text-[11px] text-emerald-700 mt-1 text-center">
      ✅ 메시지방에 완료 보고 발송 {whenLabel(doneNoticeAt, today)}
    </p>
  ) : noticeKind === 'sending' ? (
    <p className="text-[11px] text-slate-600 mt-1 text-center">
      ⏳ 메시지방에 완료 보고를 보내는 중입니다 — 잠시 뒤 새로고침해 주세요
    </p>
  ) : noticeKind === 'uncertain' ? (
    <div className="mt-1.5 bg-amber-50 border border-amber-300 rounded px-3 py-2 text-center">
      <p className="text-[11px] text-amber-900">
        ⚠ 완료 보고가 메시지방에 <b>나갔는지 확실하지 않습니다</b>
      </p>
      <p className="text-[11px] text-amber-800 mt-0.5 break-keep">
        웍스가 답을 주지 않았습니다. <b>메시지방을 먼저 확인</b>하고, 보고가 <b>없을 때만</b> 아래 단추를 눌러 주세요.
      </p>
      {d?.doneNoticeError && (
        <p className="text-[10px] text-amber-700/80 mt-0.5 break-all">{d.doneNoticeError}</p>
      )}
      {canEdit && (
        <button
          type="button"
          onClick={() => void retryNotice(true)}
          disabled={retryBusy}
          className="mt-1.5 px-4 min-h-[40px] bg-white border border-amber-400 rounded text-xs text-amber-900 disabled:opacity-50"
        >
          {retryBusy ? '보내는 중…' : '방에 없음 — 다시 보내기'}
        </button>
      )}
    </div>
  ) : (
    <div className="mt-1.5 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-center">
      <p className="text-[11px] text-amber-800">
        ⚠ 메시지방에 완료 보고가 <b>나가지 않았습니다</b>
        {d?.doneNoticeError ? ` — ${d.doneNoticeError}` : ''}
      </p>
      {canEdit && (
        <button
          type="button"
          onClick={() => void retryNotice()}
          disabled={retryBusy}
          className="mt-1.5 px-4 min-h-[40px] bg-white border border-amber-400 rounded text-xs text-amber-800 disabled:opacity-50"
        >
          {retryBusy ? '보내는 중…' : '다시 보내기'}
        </button>
      )}
    </div>
  );

  if (q.isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="text-center text-slate-400 py-16"><LoadingLines /></div>
      </div>
    );
  }
  if (q.isError && !q.data) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="text-center text-red-500 py-16 text-sm px-4">
          오류: {(q.error as Error).message}
        </div>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="text-center text-slate-400 py-16 text-sm">주요재고로 지정된 품목이 없습니다.</div>
      </div>
    );
  }

  // 옛 서버엔 조치 화면·미리보기가 없다 · 가리킬 품목이 없으면 흰 화면 대신 목록
  let shown: View = view;
  if ((shown === 'fix' || shown === 'preview') && !isNew) shown = 'list';
  if (shown === 'card' && !cur) shown = 'list';

  /* ── 📊 미리보기 ─────────────────────────────── */
  if (shown === 'preview' && gate) {
    const sentOk = sendResult?.ok === true;
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {header}
        {progress}
        {bands}
        <main className="flex-1 px-3 py-3 pb-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="text-sm font-bold text-slate-800">📊 재고현황 표 미리보기</h2>
            {imgState === 'ok' && (
              <button
                type="button"
                onClick={() => setZoom((z) => !z)}
                className="px-3 min-h-[36px] bg-white border border-slate-300 rounded text-xs text-slate-700 active:bg-slate-100"
              >
                {zoom ? '화면에 맞추기' : '🔍 크게 보기'}
              </button>
            )}
          </div>
          <div className="bg-white rounded shadow-sm p-2 overflow-auto">
            {previewT !== null && (
              <img
                key={previewT}
                src={checkApi.statusPreviewUrl(previewT)}
                alt="재고현황 표 미리보기"
                onLoad={() => setImgState('ok')}
                onError={() => setImgState('error')}
                className={imgState !== 'ok' ? 'hidden' : zoom ? 'max-w-none' : 'w-full h-auto'}
              />
            )}
            {imgState === 'loading' && (
              <div className="py-16 text-center text-sm text-slate-500">표 만드는 중… (3~5초)</div>
            )}
            {imgState === 'error' && (
              <div className="py-10 text-center">
                <p className="text-sm text-red-600 mb-3">표를 만들지 못했습니다</p>
                <button
                  type="button"
                  onClick={reloadPreview}
                  className="px-5 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-700 active:bg-slate-100"
                >
                  다시 불러오기
                </button>
              </div>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-2 text-center">보낼 때 그 순간 수량·시각으로 다시 만듭니다.</p>
          {sendResult && (
            <p
              className={`mt-3 text-sm text-center font-semibold break-words ${
                sendResult.ok ? 'text-emerald-700' : 'text-red-600'
              }`}
            >
              {sendResult.text}
            </p>
          )}
          {!gate.pass && gate.message && (
            <p className="mt-3 text-xs text-amber-800 text-center break-words">{gate.message}</p>
          )}
        </main>
        <div
          className="sticky bottom-0 bg-white border-t border-slate-200 px-3 py-2"
          style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        >
          {canEdit && gate.pass && !sentOk && (
            <button
              type="button"
              onClick={() => void sendStatusNow()}
              disabled={statusBusy}
              className="w-full min-h-[52px] bg-blue-600 text-white rounded font-bold text-base disabled:opacity-50 active:bg-blue-700"
            >
              {statusBusy ? '보내는 중…' : '이대로 메신저로 보내기'}
            </button>
          )}
          <button
            type="button"
            onClick={() => openView(previewFrom === 'preview' ? 'done' : previewFrom)}
            disabled={statusBusy}
            className="w-full mt-2 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-700 disabled:opacity-50 active:bg-slate-100"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  /* ── 🔧 조치 화면 ────────────────────────────── */
  if (shown === 'fix' && gate) {
    const openItems = items.filter((i) => i.check?.status === 'diff');
    const doneItems = items.filter((i) => i.check?.status === 'resolved');
    const remain = Math.max(0, counts.total - counts.checked);
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {header}
        {progress}
        {bands}
        <main className="flex-1 px-3 py-3 pb-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
            <h2 className="text-base font-bold text-slate-900">🔧 조치 필요 {openItems.length}건</h2>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => void q.refetch()}
                disabled={q.isFetching}
                className="px-3 min-h-[40px] bg-white border border-slate-300 rounded text-xs text-slate-700 disabled:opacity-50 active:bg-slate-100"
              >
                {q.isFetching ? '읽는 중…' : '↻ 지금 숫자 다시 보기'}
              </button>
              <button
                type="button"
                onClick={() => openView('list')}
                className="px-3 min-h-[40px] bg-white border border-slate-300 rounded text-xs text-slate-700 active:bg-slate-100"
              >
                목록
              </button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">
            매입·출고·회수 기록을 고친 뒤, 다시 세어 지금 시스템 숫자와 같으면 통과시킵니다. 재고 숫자는 여기서
            바뀌지 않습니다.
          </p>

          {openItems.length === 0 ? (
            <div className="bg-white rounded shadow-sm px-4 py-6 text-center">
              <div className="text-4xl mb-2">✅</div>
              {gate.pass ? (
                <>
                  <p className="text-sm font-semibold text-slate-800 mb-3">
                    조치 필요 0건 — 이제 재고현황 표를 보낼 수 있습니다
                  </p>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={openPreview}
                      className="w-full min-h-[52px] bg-blue-600 text-white rounded font-semibold active:bg-blue-700"
                    >
                      📊 표 미리 보고 보내기
                    </button>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-slate-800 mb-3">
                    조치 필요 0건 — 남은 {remain}개를 확인하면 재고현황 표를 보낼 수 있습니다
                  </p>
                  <button
                    type="button"
                    onClick={goFirstUnchecked}
                    className="w-full min-h-[52px] bg-emerald-600 text-white rounded font-semibold active:bg-emerald-700"
                  >
                    남은 품목 이어서 확인
                  </button>
                </>
              )}
            </div>
          ) : (
            openItems.map((it) =>
              it.check ? (
                <FixCard
                  key={it.itemCode}
                  item={it}
                  mark={it.check}
                  reasons={reasons}
                  today={today}
                  canEdit={canEdit}
                  canUndo={!doneNoticeAt}
                  busy={busy}
                  onResolve={resolveItem}
                  onFixDiff={fixDiffItem}
                  onUndo={(i) => void undo(i)}
                />
              ) : null,
            )
          )}

          {doneItems.length > 0 && (
            <section className="mt-5">
              <h3 className="text-xs font-semibold text-slate-500 mb-1.5 px-1">조치 완료 {doneItems.length}건</h3>
              <ul className="space-y-1.5">
                {doneItems.map((it) => (
                  <li key={it.itemCode}>
                    <button
                      type="button"
                      onClick={() => goto(items.indexOf(it))}
                      className="w-full text-left bg-white rounded shadow-sm px-3 py-2.5 flex items-center gap-2 active:bg-slate-100"
                    >
                      <span className="text-lg w-6 shrink-0 text-center">🔧</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-slate-900 truncate">{nameOf(it)}</span>
                        <span className="block text-[11px] text-slate-500 truncate">
                          {it.check?.resolved
                            ? `통과 ${who(it.check.resolved.byName, it.check.resolved.by)} ${whenLabel(it.check.resolved.at, today)} · ${it.check.resolved.reasonLabel}`
                            : '조치 완료'}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* 조치 화면에서도 [처음부터 다시]로 갈 수 있게 — 마침 화면까지 돌아가는 길이 멀었다 (v2.202.1) */}
          {isNew && canEdit && !!d?.canRestart && counts.checked >= 1 && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => void restartRound()}
                disabled={busy}
                className="mt-8 px-4 min-h-[44px] text-xs text-slate-400 underline disabled:opacity-50 active:text-slate-600"
              >
                🔄 처음부터 다시 검수하기
              </button>
            </div>
          )}
        </main>
      </div>
    );
  }

  /* ── 마침 화면 ───────────────────────────────── */
  if (shown === 'done') {
    const openItems = items.filter((i) => i.check?.status === 'diff');
    const remain = Math.max(0, counts.total - counts.checked);
    // (a) 아직 남음 · (b) 다 셌고 조치 필요 있음 · (c) 다 셌고 조치 필요 0
    // 옛 서버엔 「조치 필요」 문턱이 없다 → (b) 로 보내지 않는다
    const phase: 'a' | 'b' | 'c' = !allChecked ? 'a' : counts.open > 0 && isNew ? 'b' : 'c';
    const countLine = [
      `${counts.total}개 중 ${counts.checked}개 확인`,
      counts.open > 0 ? `조치 필요 ${counts.open}건` : '',
      counts.resolved > 0 ? `조치 완료 ${counts.resolved}건` : '',
    ].filter(Boolean).join(' · ');
    const showRestart = isNew && canEdit && !!d?.canRestart && counts.checked >= 1;
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {header}
        {progress}
        {bands}
        <main className="flex-1 px-4 py-6 text-center">
          <div className="text-5xl mb-3">{phase === 'a' ? '📋' : '✅'}</div>
          <div className="text-lg font-bold text-slate-900 mb-1">
            {phase === 'a'
              ? `${remain}개가 남았습니다`
              : phase === 'b'
                ? `${counts.total}종 확인을 마쳤습니다`
                : '이번 주 확인을 마쳤습니다'}
          </div>
          <div className={`text-sm mb-5 ${phase === 'b' ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>
            {phase === 'b'
              ? `조치 필요 ${counts.open}건 — 조치가 끝나야 재고현황 표를 보낼 수 있습니다`
              : countLine}
            {phase === 'c' && d?.stage === 'sent' && isNew && (
              <span className="block text-emerald-700 mt-0.5">📊 재고현황 표도 보냈습니다</span>
            )}
          </div>

          {openItems.length > 0 && (
            <ul className="text-left mb-4 space-y-2">
              {openItems.map((it) => (
                <li key={it.itemCode} className="bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <div className="text-sm font-semibold text-slate-900">{nameOf(it)}</div>
                  <div className="text-xs text-amber-800 mt-0.5 break-words">
                    {it.check ? diffNumbers(baselineOf(it.check), it.check.actualQty) : ''}
                    {it.check?.note ? ` — ${it.check.note}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {phase === 'b' && isNew && (
            <button
              type="button"
              onClick={() => enterFix()}
              className="w-full mb-4 min-h-[56px] bg-amber-500 text-white rounded font-bold text-base active:bg-amber-600"
            >
              🔧 조치 필요 {counts.open}건 보기
            </button>
          )}

          {/* 완료 보고가 방에 실제로 나갔는지부터 보여 준다 */}
          <div className="text-left mb-3">{noticeBlock}</div>

          {phase !== 'b' && <div className="text-left mb-4">{statusArea}</div>}

          <div className="flex flex-col gap-2">
            {phase === 'a' && (
              <button
                type="button"
                onClick={goFirstUnchecked}
                className="w-full min-h-[52px] bg-emerald-600 text-white rounded font-semibold active:bg-emerald-700"
              >
                남은 품목 이어서 확인
              </button>
            )}
            <button
              type="button"
              onClick={() => openView('list')}
              className="w-full min-h-[48px] bg-slate-800 text-white rounded font-semibold active:bg-slate-700"
            >
              목록으로 보기
            </button>
          </div>

          {showRestart && (
            <button
              type="button"
              onClick={() => void restartRound()}
              disabled={busy}
              className="mt-10 px-4 min-h-[44px] text-xs text-slate-400 underline disabled:opacity-50 active:text-slate-600"
            >
              🔄 처음부터 다시 검수하기
            </button>
          )}
        </main>
      </div>
    );
  }

  /* ── 목록 화면 ───────────────────────────────── */
  if (shown === 'list' || !cur) {
    const showOnlyOpen = onlyOpen && counts.open > 0;
    const rows = items
      .map((it, n) => ({ it, n }))
      .filter(({ it }) => !showOnlyOpen || it.check?.status === 'diff');
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {header}
        {progress}
        {bands}
        <main className="flex-1 px-3 py-3 pb-20">
          {counts.open > 0 && (
            <button
              type="button"
              onClick={() => setOnlyOpen((v) => !v)}
              className={`w-full mb-2 min-h-[44px] rounded text-sm font-semibold border ${
                showOnlyOpen
                  ? 'bg-amber-500 border-amber-500 text-white active:bg-amber-600'
                  : 'bg-white border-amber-300 text-amber-700 active:bg-amber-50'
              }`}
            >
              {showOnlyOpen ? '전체 보기' : `⚠ 조치 필요만 보기 (${counts.open})`}
            </button>
          )}
          <ul className="space-y-1.5">
            {rows.map(({ it, n }) => (
              <li key={it.itemCode}>
                <button
                  type="button"
                  onClick={() => goto(n)}
                  className="w-full text-left bg-white rounded shadow-sm px-3 py-2.5 flex items-center gap-2 active:bg-slate-100"
                >
                  <span className="text-lg w-6 shrink-0 text-center">{statusIcon(it.check)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-slate-900 truncate">{nameOf(it)}</span>
                    <span className="block text-[11px] text-slate-400 truncate">
                      {it.displayGroup ?? it.itemGroup ?? '기타'} · 현재고 {fmt(it.currentStock)}
                      {it.check?.status === 'diff' ? ' · 조치 필요' : ''}
                      {it.check?.status === 'resolved' ? ' · 조치 완료' : ''}
                    </span>
                  </span>
                  {it.prevDiff && (
                    <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded shrink-0">
                      지난주
                    </span>
                  )}
                  {it.short && (
                    <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded shrink-0">
                      부족
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </main>
        <div
          className="sticky bottom-0 bg-white border-t border-slate-200 px-3 py-2"
          style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={() => goto(Math.min(idx, items.length - 1))}
            className="w-full min-h-[48px] bg-slate-800 text-white rounded font-semibold active:bg-slate-700"
          >
            하나씩 확인으로 돌아가기
          </button>
          {/* 📊 수량을 고친 뒤 나중에 다시 들어와 누르는 자리가 여기다 */}
          {statusArea}
          {/* 🔔 관리자만 — 월요일에 나갈 글을 지금 한 통 보내 본다 (맨 앞에 [시험발송] 이 붙는다) */}
          {isAdmin && (
            <button
              type="button"
              onClick={() => void sendTest()}
              disabled={testBusy}
              className="w-full mt-2 py-2 bg-white border border-slate-300 rounded text-xs text-slate-600 disabled:opacity-50 active:bg-slate-100"
            >
              {testBusy ? '보내는 중…' : '🔔 월요일에 나갈 글 시험 발송 (메시지방으로)'}
            </button>
          )}
          {testMsg && (
            <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-slate-700 whitespace-pre-wrap break-all">
              {testMsg}
            </pre>
          )}
        </div>
      </div>
    );
  }

  /* ── 카드 화면 (하나씩) ───────────────────────── */
  const item = cur;
  const mark = item.check;
  const st = mark?.status ?? null;
  const baseline = mark ? baselineOf(mark) : null;
  const reportLocked = doneNoticeAt !== null;
  const fixMode = isNew && st === 'diff';
  const lockText = st === 'diff'
    ? '완료 보고가 나간 뒤라 되돌릴 수 없습니다 — 맞게 바뀌었다면 [🔧 조치하러 가기]에서 통과시켜 주세요'
    : '완료 보고가 나간 뒤라 되돌릴 수 없습니다 — 다르면 [✏️ 다름]으로 고쳐 적어 주세요';
  const undoRow = !canEdit ? null : reportLocked ? (
    <p className="mt-1.5 text-[11px] text-slate-500">{lockText}</p>
  ) : (
    <button
      type="button"
      onClick={() => void undo(item)}
      disabled={busy}
      className="w-full mt-2 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-600 disabled:opacity-50 active:bg-slate-100"
    >
      ↩ 되돌리기
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {header}
      {progress}
      {bands}

      <main className="flex-1 px-3 py-3 pb-4">
        <div className="bg-white rounded shadow-sm px-4 py-4">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="text-[11px] font-semibold text-slate-500">
              {item.displayGroup ?? item.itemGroup ?? '기타'} · {idx + 1}번째 / {items.length}
            </span>
            {item.short && (
              <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
                부족
              </span>
            )}
          </div>

          <div ref={nameRef} className="text-lg font-bold text-slate-900 leading-snug break-words">{nameOf(item)}</div>
          <div className="text-[11px] text-slate-400 font-mono break-all mb-3">{item.itemCode}</div>

          {item.prevDiff && (
            <div className="mb-3 rounded bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-800 break-words">
              지난주 ‘조치 필요’로 끝난 품목 — {diffNumbers(item.prevDiff.baselineQty, item.prevDiff.actualQty)}
              {item.prevDiff.note ? ` · ${item.prevDiff.note}` : ''}
            </div>
          )}

          {/* 🧮 계산기가 펼쳐진 채 **낮은 화면**(높이 760 이하 · 작은 폰을 웍스 앱 안에서 열 때 등)이면 현재고를 한 줄로
              줄인다 — 숫자판 맨 아랫줄까지 한 화면에 들어오게(v2.207.1 실측). 큰 화면·계산기 접힘은 예전 모양 그대로 */}
          <div
            className={`relative bg-slate-50 rounded py-3 text-center mb-3 ${
              calcOpen ? '[@media(max-height:760px)]:py-1.5 [@media(max-height:760px)]:pl-3 [@media(max-height:760px)]:text-left' : ''
            }`}
          >
            <div
              className={`text-xs text-slate-500 mb-0.5 ${
                calcOpen ? '[@media(max-height:760px)]:inline-block [@media(max-height:760px)]:mr-2 [@media(max-height:760px)]:mb-0' : ''
              }`}
            >
              현재고
            </div>
            <div
              className={`text-4xl font-bold text-slate-900 tabular-nums ${
                calcOpen ? '[@media(max-height:760px)]:inline-block [@media(max-height:760px)]:text-3xl' : ''
              }`}
            >
              {fmt(item.currentStock)}
            </div>
            <CalcToggle
              open={calcOpen}
              onClick={toggleCalc}
              className={`absolute right-2 top-2 ${
                calcOpen ? '[@media(max-height:760px)]:top-1/2 [@media(max-height:760px)]:-translate-y-1/2' : ''
              }`}
            />
          </div>

          {/* 📍 장소별 센 수 (v2.226.0) — 10층 공장 · B1 창고. 빈 칸은 0 · 합계가 현재고와 같으면 맞음 */}
          {locMode && canEdit && !diffOpen && (
            <div className="mb-3">
              <LocationQtyFields
                locations={locations}
                draft={locDraft}
                onChange={setLocDraft}
                active={locActive}
                onActive={setLocActive}
                refQty={item.currentStock}
                disabled={busy}
                diffHint="다르면 그대로 [⚠ 다름]으로 저장됩니다 — 메모는 📝"
              />
            </div>
          )}

          {calcOpen && (
            <div ref={calcRef} className="mb-3 scroll-mt-14 scroll-mb-24">
              <CountCalculator
                state={calcByItem[item.itemCode] ?? CALC_EMPTY}
                onChange={(update) =>
                  setCalcByItem((prev) => ({ ...prev, [item.itemCode]: update(prev[item.itemCode] ?? CALC_EMPTY) }))
                }
                refQty={item.currentStock}
                refLabel="현재고"
                {...(canEdit && st !== 'resolved'
                  ? locMode && !diffOpen ? {
                      applyLabel: (n: number) => `↑ ${locLabel(locActive)} 칸에 ${fmt(n)} 넣기`,
                      onApply: applyCalcQty,
                      applyWhenSame: true,
                    } : {
                      applyLabel: (n: number) =>
                        diffOpen
                          ? `↓ 실제 수량 칸에 ${fmt(n)} 넣기`
                          : st === 'diff'
                            ? `✏️ 실제 ${fmt(n)}개로 고쳐 적기`
                            : `✏️ 실제 ${fmt(n)}개로 다름 적기`,
                      onApply: applyCalcQty,
                      applyWhenSame: diffOpen,
                    }
                  : {})}
              />
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            <div className="flex justify-between"><dt className="text-slate-500">가용</dt>
              <dd className={item.short ? 'font-semibold text-red-600' : 'text-slate-800'}>{fmt(item.availableStock)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">안전재고</dt>
              <dd className="text-slate-800">{item.safeStock ? fmt(item.safeStock) : '-'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">납품예정</dt>
              <dd className="text-slate-800">{fmt(item.pendingDelivery)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">입고예정</dt>
              <dd className="text-slate-800">{fmt(item.incoming)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">임대</dt>
              <dd className="text-slate-800">{fmt(item.leased)}</dd></div>
          </dl>

          {/* 📷 이 품목 사진 — 이번 주·이번 회차. 바이트는 NAS 에 있고 기록만 포털에(v2.224.0) */}
          {d && (
            <StockPhotoStrip
              photoKey={{ scope: 'weekly', weekDate: d.weekDate, round }}
              itemCode={item.itemCode}
              canEdit={canEdit}
              me={user ? { userId: user.userId, role: user.role } : null}
            />
          )}

          {mark && st === 'ok' && (
            <div className="mt-3">
              <div className="rounded px-3 py-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800">
                <span className="font-semibold">✅ 맞음</span> · {who(mark.checkedByName, mark.checkedBy)}{' '}
                {whenLabel(mark.checkedAt, today)}
                {mark.locQty && <div className="mt-0.5">{locText(mark.locQty, locations)}</div>}
                {mark.note && <div className="mt-0.5 text-slate-700 break-words">📝 {mark.note}</div>}
              </div>
              {undoRow}
            </div>
          )}

          {mark && st === 'diff' && (
            <div className="mt-3">
              <div className="rounded px-3 py-2 text-xs bg-amber-50 border border-amber-300 text-amber-900 break-words">
                <div className="font-semibold">
                  ⚠ 조치 필요 · 발견 {who(mark.checkedByName, mark.checkedBy)} {whenLabel(mark.checkedAt, today)}
                </div>
                <div className="mt-0.5">그때 {diffNumbers(baseline, mark.actualQty)}</div>
                {mark.locQty && <div className="mt-0.5">{locText(mark.locQty, locations)}</div>}
                {mark.note && <div className="mt-0.5">{mark.note}</div>}
              </div>
              {undoRow}
            </div>
          )}

          {mark && st === 'resolved' && (
            <div className="mt-3 space-y-1.5">
              <div className="rounded px-3 py-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 break-words">
                <div>
                  <span className="font-semibold">발견</span> {who(mark.checkedByName, mark.checkedBy)}{' '}
                  {whenLabel(mark.checkedAt, today)} · {diffNumbers(baseline, mark.actualQty)}
                </div>
                {mark.note && <div className="mt-0.5">{mark.note}</div>}
              </div>
              <div className="rounded px-3 py-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 break-words">
                <div>
                  <span className="font-semibold">🔧 통과</span>{' '}
                  {mark.resolved
                    ? `${who(mark.resolved.byName, mark.resolved.by)} ${whenLabel(mark.resolved.at, today)} · 다시 세어 ${fmt(mark.resolved.qty)} · ${mark.resolved.reasonLabel}`
                    : '조치 완료'}
                </div>
                {mark.resolved?.note && <div className="mt-0.5">{mark.resolved.note}</div>}
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => void unresolveItem(item)}
                  disabled={busy}
                  className="w-full min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-600 disabled:opacity-50 active:bg-slate-100"
                >
                  ↩ 조치 완료 취소
                </button>
              )}
            </div>
          )}

          {/* 📝 메모 (장소 칸 모드) — 맞음·다름 모두 함께 남는다 (v2.322.0 · #31 · 전에는 다름일 때만) */}
          {locMode && memoOpen && canEdit && !diffOpen && (
            <div className="mt-3 border-t border-slate-200 pt-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">메모 <span className="font-normal text-slate-400">(맞음·다름 모두 함께 남습니다)</span></label>
              {diffChips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {diffChips.map((c) => (
                    <button key={c} type="button" onClick={() => addChip(c)}
                      className="px-2.5 min-h-[40px] rounded-full border border-amber-300 bg-white text-xs text-amber-800 active:bg-amber-50">+ {c}</button>
                  ))}
                </div>
              )}
              <textarea value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="예) 2개는 B1 창고 선반 뒤에 따로 있음"
                className="w-full border border-slate-300 rounded px-3 py-2 text-base focus:outline-none focus:border-amber-500" />
            </div>
          )}

          {diffOpen && canEdit && (
            <div ref={diffPanelRef} className="mt-3 border-t border-slate-200 pt-3 scroll-mb-28">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                실제로 센 수량 <span className="font-normal text-slate-400">(모르면 비워 두고 메모만)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={actualQty}
                onChange={(e) => { setActualQty(e.target.value); setSameAck(false); }}
                placeholder={String(item.currentStock)}
                className={`w-full border rounded px-3 py-2.5 text-base focus:outline-none ${
                  actualBad ? 'border-red-400 focus:border-red-500' : 'border-slate-300 focus:border-amber-500'
                }`}
              />
              {actualBad && (
                <p className="text-[11px] text-red-600 mt-1">실제 수량은 0 이상 숫자로 적어 주세요</p>
              )}
              {same && !sameAck && (
                <div className="mt-2 rounded bg-amber-50 border border-amber-300 px-3 py-2">
                  <p className="text-xs font-semibold text-amber-900">
                    센 수량이 시스템 수량({fmt(item.currentStock)})과 같습니다
                  </p>
                  {fixMode && (
                    <p className="text-[11px] text-amber-800 mt-0.5">
                      장부가 맞게 고쳐졌다면 [🔧 조치하러 가기]에서 통과시켜 주세요
                    </p>
                  )}
                  <div className="flex gap-2 mt-2">
                    {fixMode ? (
                      <button
                        type="button"
                        onClick={() => enterFix(item.itemCode)}
                        className="flex-1 min-h-[44px] bg-amber-500 text-white rounded text-sm font-semibold active:bg-amber-600"
                      >
                        🔧 조치하러 가기
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { clearDiff(); void save('ok'); }}
                        disabled={busy}
                        className="flex-1 min-h-[44px] bg-emerald-600 text-white rounded text-sm font-semibold disabled:opacity-50 active:bg-emerald-700"
                      >
                        맞음으로 적기
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setSameAck(true)}
                      className="flex-1 min-h-[44px] bg-white border border-amber-400 rounded text-sm text-amber-800 active:bg-amber-100"
                    >
                      그래도 다름
                    </button>
                  </div>
                </div>
              )}

              <label className="block text-xs font-semibold text-slate-700 mt-3 mb-1">메모</label>
              {diffChips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {diffChips.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => addChip(c)}
                      className="px-2.5 min-h-[40px] rounded-full border border-amber-300 bg-white text-xs text-amber-800 active:bg-amber-50"
                    >
                      + {c}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="예) 2개는 창고 B 에 따로 있음"
                className="w-full border border-slate-300 rounded px-3 py-2 text-base focus:outline-none focus:border-amber-500"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                적어 두기만 합니다 — 재고 숫자는 바뀌지 않습니다.
              </p>
            </div>
          )}

          {err && <div ref={errRef} className="mt-3 text-xs text-red-600 break-words scroll-mb-28">{err}</div>}
        </div>

        {/* 앞뒤 이동 */}
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={() => { if (confirmLeave()) goto(idx - 1); }}
            disabled={idx === 0}
            className="flex-1 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-700 disabled:opacity-40 active:bg-slate-100"
          >
            ← 이전
          </button>
          <button
            type="button"
            onClick={() => { if (confirmLeave()) openView('list'); }}
            className="px-4 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-700 active:bg-slate-100 whitespace-nowrap"
          >
            목록
          </button>
          <button
            type="button"
            onClick={() => {
              if (!confirmLeave()) return;
              if (idx === items.length - 1) openView('done');
              else goto(idx + 1);
            }}
            className="flex-1 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-700 active:bg-slate-100"
          >
            {idx === items.length - 1 ? '마침' : '다음 →'}
          </button>
        </div>
      </main>

      {/* 아래 고정 단추 — ref: 🧮 키패드와의 간격을 이 단추의 실제 높이(아이폰 홈 막대 포함)로 잰다 */}
      <div
        ref={dockRef}
        className="sticky bottom-0 bg-white border-t border-slate-200 px-3 py-2"
        style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
      >
        {!canEdit ? (
          <p className="text-center text-xs text-slate-500 py-2">
            조회 전용 계정은 확인 기록을 남길 수 없습니다 — 보기만 가능합니다.
          </p>
        ) : diffOpen ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={clearDiff}
              disabled={busy}
              className="px-4 min-h-[52px] bg-white border border-slate-300 rounded text-sm text-slate-700 disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="button"
              onClick={() => void save('diff')}
              disabled={busy || !diffCanSave}
              className="flex-1 min-h-[52px] bg-amber-500 text-white rounded font-bold disabled:opacity-50 active:bg-amber-600"
            >
              {busy ? '저장 중…' : st === 'diff' ? '⚠ 고쳐 적고 다음 →' : '⚠ 다름으로 적고 다음 →'}
            </button>
          </div>
        ) : fixMode ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={openDiff}
              disabled={busy}
              className="px-3 min-h-[56px] bg-white border border-amber-400 text-amber-700 rounded text-xs font-semibold leading-tight disabled:opacity-50 active:bg-amber-50"
            >
              ✏️ 다름 내용<br />고치기
            </button>
            <button
              type="button"
              onClick={() => enterFix(item.itemCode)}
              disabled={busy}
              className="flex-1 min-h-[56px] bg-amber-500 text-white rounded font-bold text-base disabled:opacity-50 active:bg-amber-600"
            >
              🔧 조치하러 가기
            </button>
          </div>
        ) : locMode ? (
          /* 📍 장소 칸 모드 (v2.226.0) — 합계로 미리 판정(저장 순간 서버가 다시 판정) */
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                // 열 때 이미 적힌 메모(맞음으로 저장된 것)를 채워 보여 준다 — 보이는 글이 곧 저장될 글
                if (!memoOpen && note.trim() === '' && curMark?.note) setNote(curMark.note);
                setMemoOpen(!memoOpen);
              }}
              disabled={busy}
              aria-pressed={memoOpen}
              className={`px-4 min-h-[52px] border rounded text-sm font-semibold disabled:opacity-50 whitespace-nowrap ${
                memoOpen ? 'bg-amber-50 border-amber-400 text-amber-800' : 'bg-white border-slate-300 text-slate-700 active:bg-slate-100'}`}
            >
              📝 메모
            </button>
            <button
              type="button"
              data-count
              onClick={() => void saveLoc()}
              disabled={busy || !locParsed.any || !!locParsed.bad}
              className={`flex-1 min-h-[52px] rounded font-bold text-base disabled:opacity-50 ${
                !locParsed.any || locParsed.bad ? 'bg-slate-300 text-slate-600'
                  : locParsed.sum === item.currentStock ? 'bg-emerald-600 text-white active:bg-emerald-700' : 'bg-amber-500 text-white active:bg-amber-600'}`}
            >
              {busy ? '저장 중…'
                : locParsed.bad ? '수량 칸을 고쳐 주세요'
                  : !locParsed.any ? '장소별 수량을 적어 주세요'
                    : locParsed.sum === item.currentStock
                      ? `✅ 맞음 (${locations.filter((l) => typeof locParsed.qty[l.key] === 'number').map((l) => fmt(locParsed.qty[l.key]!)).join('+')}) — 다음 →`
                      : `⚠ 다름 (${fmt(locParsed.sum)}) — 다음 →`}
            </button>
          </div>
        ) : st === 'resolved' ? (
          <button
            type="button"
            onClick={() => (idx === items.length - 1 ? openView('done') : goto(idx + 1))}
            className="w-full min-h-[52px] bg-slate-800 text-white rounded font-bold text-base active:bg-slate-700"
          >
            {idx === items.length - 1 ? '마침' : '다음 품목 →'}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={openDiff}
              disabled={busy}
              className="px-4 min-h-[52px] bg-white border border-amber-400 text-amber-700 rounded text-sm font-semibold disabled:opacity-50 active:bg-amber-50 whitespace-nowrap"
            >
              ✏️ 다름
            </button>
            <button
              type="button"
              onClick={() => {
                if (!confirmAgainstCalc(calcOpen, calcByItem[item.itemCode], item.currentStock, '현재고')) return;
                void save('ok');
              }}
              disabled={busy}
              className="flex-1 min-h-[52px] bg-emerald-600 text-white rounded font-bold text-base disabled:opacity-50 active:bg-emerald-700"
            >
              {busy ? '저장 중…' : mark ? '✅ 다시 확인하고 다음 →' : '✅ 확인하고 다음 →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
